import React, { useState, useRef, useEffect } from "react";
import { useWatchlist } from "@/context/WatchlistContext";
import { ChevronLeft, ChevronRight, Plus, Pencil, Trash, X, Check, MoreHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { usePriceContext } from "@/context/PriceContext";

export const WatchlistTabs: React.FC = () => {
  const { watchlists, activeWatchlistId, watchlistSymbols, setActiveWatchlist, createWatchlist, renameWatchlist, deleteWatchlist } = useWatchlist();
  const { symbols: allSymbols } = usePriceContext();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [newWatchlistName, setNewWatchlistName] = useState("");
  const [targetWatchlistId, setTargetWatchlistId] = useState<string | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const renameInputRef = useRef<HTMLInputElement>(null);
  const isMobile = useIsMobile();
  
  // State for inline editing
  const [editingWatchlistId, setEditingWatchlistId] = useState<string | null>(null);
  const [editNameValue, setEditNameValue] = useState("");
  
  // Function to get the actual count of valid symbols for a watchlist
  const getValidSymbolCount = (watchlistId: string) => {
    if (watchlistId === activeWatchlistId) {
      // For the active watchlist, use the filtered symbols from context
      return watchlistSymbols.length;
    } else {
      // For other watchlists, filter their symbols against available symbols
      const watchlist = watchlists.find(wl => wl.id === watchlistId);
      if (!watchlist) return 0;
      
      // Create a set of available symbol names for faster lookup
      const availableSymbolNames = new Set(allSymbols.map(symbol => symbol.name));
      
      // Parse symbols and count only symbols that still exist
      const parseSymbols = (symbols: string | string[]): string[] => {
        if (!symbols) return [];
        if (Array.isArray(symbols)) return symbols;
        try {
          const parsed = JSON.parse(symbols);
          return Array.isArray(parsed) ? parsed : [];
        } catch (error) {
          return [];
        }
      };
      const validSymbols = parseSymbols(watchlist.symbols);
      return validSymbols.filter(symbolName => availableSymbolNames.has(symbolName)).length;
    }
  };
  
  // Focus the input when editing starts
  useEffect(() => {
    if (editingWatchlistId && renameInputRef.current) {
      renameInputRef.current.focus();
    }
  }, [editingWatchlistId]);
  
  // ... keep existing code (handleScroll function)
  const handleScroll = (direction: "left" | "right") => {
    if (scrollContainerRef.current) {
      const scrollAmount = 200;
      const currentScroll = scrollContainerRef.current.scrollLeft;
      scrollContainerRef.current.scrollTo({
        left: direction === "left" 
          ? currentScroll - scrollAmount 
          : currentScroll + scrollAmount,
        behavior: "smooth"
      });
    }
  };
  
  // ... keep existing code (handleCreateWatchlist function)
  const handleCreateWatchlist = () => {
    if (!newWatchlistName.trim()) {
      toast.error("Watchlist name cannot be empty");
      return;
    }
    
    if (watchlists.some(wl => wl.title.toLowerCase() === newWatchlistName.toLowerCase())) {
      toast.error("A watchlist with this name already exists");
      return;
    }
    
    createWatchlist(newWatchlistName);
    setNewWatchlistName("");
    setCreateDialogOpen(false);
  };
  
  // ... keep existing code (handleDeleteWatchlist function)
  const handleDeleteWatchlist = async () => {
    if (targetWatchlistId) {
      // Close the dialog first to prevent UI freeze
      setDeleteDialogOpen(false);
      
      // Small delay to ensure dialog is fully closed
      setTimeout(async () => {
        await deleteWatchlist(targetWatchlistId);
        setTargetWatchlistId(null);
        
        // Force a UI refresh to ensure clean state
        setTimeout(() => {
          // This ensures any hanging event listeners or blocked UI is cleared
          document.body.click(); // Clear any lingering focus or popups
        }, 100);
      }, 100);
    }
  };
  
  // ... keep existing code (inline editing functions)
  const startInlineRenaming = (id: string, currentName: string) => {
    setEditingWatchlistId(id);
    setEditNameValue(currentName);
  };
  
  const cancelInlineRenaming = () => {
    setEditingWatchlistId(null);
    setEditNameValue("");
  };
  
  const confirmInlineRenaming = () => {
    if (editingWatchlistId) {
      // Check if name is empty
      if (!editNameValue.trim()) {
        toast.error("Watchlist name cannot be empty");
        return;
      }
      
      // Check for duplicate names
      if (watchlists.some(wl => wl.id !== editingWatchlistId && wl.title.toLowerCase() === editNameValue.toLowerCase())) {
        toast.error("A watchlist with this name already exists");
        return;
      }
      
      renameWatchlist(editingWatchlistId, editNameValue);
      setEditingWatchlistId(null);
      setEditNameValue("");
    }
  };
  
  const handleInlineRenameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      confirmInlineRenaming();
    } else if (e.key === 'Escape') {
      cancelInlineRenaming();
    }
  };
  
  const openDeleteDialog = (id: string) => {
    // Check if this is the default watchlist before opening the delete dialog
    if (id === "default") {
      toast.error("The default watchlist cannot be deleted");
      return;
    }
    
    setTargetWatchlistId(id);
    setDeleteDialogOpen(true);
  };
  
  // Show can scroll indicators
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  
  const checkScrollability = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft + clientWidth < scrollWidth);
    }
  };
  
  useEffect(() => {
    checkScrollability();
    window.addEventListener('resize', checkScrollability);
    return () => window.removeEventListener('resize', checkScrollability);
  }, [watchlists]);
  
  // Show empty placeholder if no watchlists
  if (watchlists.length === 0) {
    return (
      <div className="flex justify-center py-2">
        <Button
          variant="outline"
          size="sm"
          className="rounded-full"
          onClick={() => {
            setNewWatchlistName("");
            setCreateDialogOpen(true);
          }}
          data-create-watchlist
        >
          <Plus className="h-4 w-4 mr-2" />
          Create Watchlist
        </Button>
        
        {/* Create watchlist dialog */}
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>Create New Watchlist</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <Input
                name="new-watchlist-name"
                placeholder="Enter watchlist name"
                value={newWatchlistName}
                onChange={(e) => setNewWatchlistName(e.target.value)}
                autoFocus
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateWatchlist}>Create</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }
  
  return (
    <>
      <div className="relative flex items-center">
        {/* Left scroll button */}
        {canScrollLeft && (
          <Button
            variant="ghost"
            size="icon"
            className="absolute left-0 z-10 h-8 w-8 rounded-full bg-background/80 backdrop-blur-sm"
            onClick={() => handleScroll("left")}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
        )}
        
        {/* Scrollable tabs container */}
        <div 
          ref={scrollContainerRef}
          className="flex space-x-2 overflow-x-auto scrollbar-none py-2 px-1"
          onScroll={checkScrollability}
        >
          {watchlists.map((watchlist) => {
            const validSymbolCount = getValidSymbolCount(watchlist.id);
            
            return (
              <div key={watchlist.id} className="flex-shrink-0 relative group">
                {editingWatchlistId === watchlist.id ? (
                  // Inline edit mode
                  <div className="flex items-center rounded-full border bg-background px-2">
                    <Input
                      ref={renameInputRef}
                      value={editNameValue}
                      onChange={(e) => setEditNameValue(e.target.value)}
                      onKeyDown={handleInlineRenameKeyDown}
                      className="h-8 min-w-[80px] border-none p-0 focus-visible:ring-0"
                      onClick={(e) => e.stopPropagation()}
                    />
                    <div className="flex items-center space-x-1">
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-6 w-6"
                        onClick={confirmInlineRenaming}
                      >
                        <Check className="h-3 w-3" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-6 w-6"
                        onClick={cancelInlineRenaming}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  // Normal display mode
                  <div className="flex items-center">
                    <Button
                      variant={activeWatchlistId === watchlist.id ? "default" : "outline"}
                      size="sm"
                      className={cn(
                        "rounded-full",
                        activeWatchlistId === watchlist.id 
                          ? "bg-primary text-primary-foreground" 
                          : "bg-background"
                      )}
                      onClick={() => setActiveWatchlist(watchlist.id)}
                    >
                      {watchlist.title} ({validSymbolCount})
                    </Button>
                    
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 ml-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <MoreHorizontal className="h-3 w-3" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="center">
                        <DropdownMenuItem onClick={() => startInlineRenaming(watchlist.id, watchlist.title)}>
                          <Pencil className="mr-2 h-4 w-4" />
                          Rename
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => openDeleteDialog(watchlist.id)}
                          className="text-destructive focus:text-destructive"
                        >
                          <Trash className="mr-2 h-4 w-4" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                )}
              </div>
            );
          })}
          
          {/* Create new watchlist button */}
          <Button
            variant="ghost"
            size="sm"
            className={cn(
              "flex-shrink-0 rounded-full",
              isMobile ? "px-2" : "px-3"
            )}
            onClick={() => {
              setNewWatchlistName("");
              setCreateDialogOpen(true);
            }}
            data-create-watchlist
          >
            <Plus className={cn("h-4 w-4", isMobile ? "mr-0" : "mr-1")} />
            {!isMobile && <span>New List</span>}
          </Button>
        </div>
        
        {/* Right scroll button */}
        {canScrollRight && (
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-0 z-10 h-8 w-8 rounded-full bg-background/80 backdrop-blur-sm"
            onClick={() => handleScroll("right")}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        )}
      </div>
      
      {/* Create watchlist dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Create New Watchlist</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Input
              name="new-watchlist-name"
              placeholder="Enter watchlist name"
              value={newWatchlistName}
              onChange={(e) => setNewWatchlistName(e.target.value)}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateWatchlist}>Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Delete confirmation dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={(open) => {
        setDeleteDialogOpen(open);
        // If dialog is closed without clicking delete, clean up state
        if (!open) {
          setTargetWatchlistId(null);
        }
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this watchlist and remove all symbols from it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setDeleteDialogOpen(false);
              setTargetWatchlistId(null);
            }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteWatchlist}
              className="bg-destructive text-destructive-foreground"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
